<template>
    <div class="min-h-screen bg-white font-sans selection:bg-blue-100 pt-28 pb-20">
        <div class="container mx-auto px-4">

            <!-- Hero / Bio Section -->
            <transition name="fade-up" appear>
                <div class="flex flex-col items-center text-center mb-24">
                    <!-- Circular Image Container -->
                    <div class="relative mb-10 group">
                        <!-- Decorative blur ring -->
                        <div
                            class="absolute -inset-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-700">
                        </div>

                        <!-- Image -->
                        <div
                            class="relative w-56 h-56 md:w-72 md:h-72 rounded-full overflow-hidden border-[6px] border-white shadow-2xl transform transition-transform duration-700 ease-out group-hover:scale-[1.02]">
                            <img :src="artistImage" alt="Shruti - Artist"
                                class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                        </div>
                    </div>

                    <!-- Text Content -->
                    <div class="max-w-5xl mx-auto space-y-6">
                        <h1 class="text-4xl md:text-6xl font-serif text-gray-900 tracking-tight">
                            Shruti Kedia
                        </h1>
                        <p class="text-sm md:text-base uppercase tracking-[0.2em] text-blue-600 font-medium">
                            Fine Artist & Illustrator
                        </p>

                        <div class="prose prose-lg text-gray-600 leading-relaxed mt-8">
                            <p>
                                Hi, I’m Shruti – the heart, hands,
                                and eyes behind ArtPulse by
                                Shruti.
                                Art has always been more than
                                just a skill for me – it’s my way of
                                connecting, feeling, and telling
                                stories without words.
                            </p>
                            <p>
                                What started as a childhood
                                passion slowly bloomed into a
                                lifelong journey lled with
                                portraits, sketches, and colors
                                that speak emotions. I specialize
                                in realistic sketching, expressive
                                watercolor work, and
                                storytelling through brush and
                                ink. Whether it’s a quiet moment
                                captured in a sketch or the
                                vibrant spirit of a festival, each
                                piece I create re ects a piece of
                                my soul.
                            </p>
                            <p>
                                I also love teaching – sharing
                                what I’ve learned with kids and
                                adults who are just beginning
                                their creative paths. Watching
                                their joy after nishing a piece is
                                one of the most ful lling parts
                                of my journey.
                            </p>
                            <p>
                                I believe that art doesn’t need to
                                be perfect; it just needs to be
                                honest.
                                Thank you for being a part of
                                my world through this
                                catalogue.
                                Let’s keep creating, feeling, and
                                celebrating the beauty around
                                us.
                            </p>
                        </div>

                        <!-- Social / Contact Links -->
                        <div class="flex justify-center gap-6 pt-6">
                            <a href="https://www.youtube.com/@artpulse_by_shruti" target="_blank"
                                class="text-gray-400 hover:text-red-600 transition-colors duration-300">
                                <span class="sr-only">YouTube</span>
                                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M19.615 3.184a1 1 0 00-.872-.184L4 8v8l14.743 5.001a1 1 0 00.872-.184 1 1 0 00.308-.872V4.056a1 1 0 00-.308-.872z" />
                                </svg>
                            </a>
                            <a href="https://www.instagram.com/artpulse_by_shruti/" target="_blank"
                                class="text-gray-400 hover:text-pink-600 transition-colors duration-300">
                                <span class="sr-only">Instagram</span>
                                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6zm4.5-3a1 1 0 110 2 1 1 0 010-2z" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </transition>

            <!-- Featured Works -->
            <div class="border-t border-gray-100 pt-20">
                <div class="text-center mb-16">
                    <h2 class="text-3xl md:text-4xl font-serif text-gray-900 mb-3">Selected Works</h2>
                    <p class="text-gray-500 font-light">A glimpse into my recent creations</p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                    <div v-for="(work, index) in works" :key="work.id || index" class="group cursor-pointer">
                        <div class="relative overflow-hidden bg-gray-100 shadow-md rounded-sm aspect-[4/5] mb-6">
                            <img :src="work.image" :alt="work.title"
                                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                loading="lazy" />
                            
                            <div
                                class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500">
                            </div>
                        </div>

                        <div class="text-center">
                            <h3
                                class="text-xl font-serif text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                                {{ work.title }}
                            </h3>
                            <p class="text-sm text-gray-500 mt-1 uppercase tracking-wider">{{ work.category }}</p>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
// import api from '@/services/api';

export default {
    name: 'AboutUs',
    data() {
        return {
            artistImage: 'https://assets-allwebsites.s3.ap-south-1.amazonaws.com/artpulse/shruti.jpeg',
            works: []
        }
    },
    mounted() {
        // this.fetchWorks();
    },
    methods: {
        // async fetchWorks() {
        //     try {
        //         // Fetching a few products to display as "Selected Works"
        //         let param = {
        //             limit: 6, // Fetching 6 items for a balanced grid
        //             fetchColumns: 'id,name,productmedia,product_category'
        //         }
        //         const response = await api.post('api/product/getwithcategories', param);
        //         const products = response.data?.data || [];

        //         this.works = products.map(product => ({
        //             id: product.id,
        //             title: product.name,
        //             image: product.productmedia?.[0]?.small_fullurl || '',
        //             category: product.product_category?.[1]?.name ?? 'Artwork'
        //         })).filter(w => w.image);

        //     } catch (error) {
        //         console.error('Failed to fetch works:', error);
        //     }
        // }
    }
}
</script>

<style scoped>
.fade-up-enter-active,
.fade-up-leave-active {
    transition: opacity 1s ease, transform 1s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
    opacity: 0;
    transform: translateY(40px);
}
</style>